/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, signal} from '@angular/core';
import {IFilterAngularComp} from 'ag-grid-angular';
import {IDoesFilterPassParams, IFilterParams} from 'ag-grid-community';
import {Observable} from 'rxjs';
import {DropdownHelperService} from '../../../service/dropdown-helper.service';

/** Extra colDef.filterParams the grid supplies when its query runs on the server. AG Grid copies these
 * onto the params object handed to agInit(). */
interface ServerFilterParams {
    serverSideFiltering?: boolean;
    distinctValuesProvider?: () => Observable<string[]>;
    isLocked?: () => boolean;
}

@Component({
    selector: 'app-custom-multiselect-filter',
    template: `
    <p-multiSelect *ngIf="guiAttached()"
                   [options]="options"
                   [ngModel]="selectedValues"
                   (ngModelChange)="onSelectedValuesChanged($event)"
                   optionLabel="label" optionValue="value" [showClear]="showClear()"
                   [disabled]="disabled()"
                   display="chip" [placeholder]="filterPlaceholder">
    </p-multiSelect>
  `,
    styles: [],
    standalone: false
})

export class CustomMultiSelectFilterComponent implements IFilterAngularComp {
  private params: IFilterParams;
  public options: any[] = [];
  public multiSelectField = false;
  public selectedValues: string[] = [];
  public filterPlaceholder = 'Select Values';
  // True when options are derived from live grid data (getUniqueValues()) rather than a fixed
  // cellEditorParams.availableOptions list; only derived option lists are refreshed on reopen.
  private useDerivedOptions = false;
  // Set by the grid when the query behind it runs on the server: the rows in memory are one page of a
  // larger result, so this filter must neither derive its options from them nor re-apply itself to them.
  private serverSideFiltering = false;
  private distinctValuesProvider?: () => Observable<string[]>;
  private serverOptionsLoaded = false;
  private isLocked = () => false;
  // AG Grid (ag-grid-angular <32, tracked as AG-11633) creates this Angular framework filter
  // component - and inserts its host element into the DOM via ViewContainerRef - as soon as the
  // filter instance is first needed (e.g. gridApi.setFilterModel() for a column whose filter menu
  // has never been opened, such as our default status filters applied on grid load), well before
  // AG Grid relocates that element into the real filter popup (.ag-menu/.ag-popup). Until relocated,
  // this produces a visible "escaped" dropdown control below the grid. Keep the real control out of
  // the DOM until afterGuiAttached() confirms AG Grid has actually attached the GUI in its intended
  // popup location (either because the user opened the filter, or because AG Grid moved it there).
  public readonly guiAttached = signal(false);

  agInit(params: IFilterParams): void {
    this.params = params;
    const serverParams = params as unknown as ServerFilterParams;
    this.serverSideFiltering = serverParams.serverSideFiltering === true;
    this.distinctValuesProvider = serverParams.distinctValuesProvider;
    this.isLocked = serverParams.isLocked ?? (() => false);
    const cellEditorParams = this.params.column.getColDef().cellEditorParams;
    if (cellEditorParams?.availableOptions) {
      this.useDerivedOptions = false;
      const allowedValueKey = cellEditorParams.allowedValueKey;
      const allowedValues = cellEditorParams.allowedValues ?? [];
        if (allowedValueKey) {
            // because we have an allowedValue key, we want to get the value from each of the options using that key
            this.options = CustomMultiSelectFilterComponent.sortOptions(cellEditorParams.availableOptions
                .filter(value => value != null) // Filter out null and undefined
                .map(value => {
                    if (value && typeof value === 'object' && value[allowedValueKey]) {
                        const extractedValue = value[allowedValueKey];
                        // Only include if the extracted value is a valid string/number
                        if (extractedValue != null && typeof extractedValue !== 'object') {
                            return {
                                label: DropdownHelperService.labelWithDescription(extractedValue, allowedValues),
                                value: String(extractedValue)
                            };
                        }
                        return null;
                    }
                    // If value is an object without allowedValueKey, skip it
                    if (typeof value === 'object') {
                        return null;
                    }
                    return {
                        label: DropdownHelperService.labelWithDescription(value, allowedValues),
                        value: String(value)
                    };
                })
                .filter(option => option != null)); // Remove any null entries from mapping
        } else {
            this.options = CustomMultiSelectFilterComponent.sortOptions(cellEditorParams.availableOptions
                .filter(value => value != null && typeof value !== 'object') // Filter out null, undefined, and objects
                .map(value => ({
                    label: DropdownHelperService.labelWithDescription(value, allowedValues),
                    value: String(value)
                })));
        }
        // A maxSelectionCount of '0' means unlimited. '1' means 'single selection', so anything else is multi select.
        this.multiSelectField = cellEditorParams.maxSelectionCount !== 1;
    } else if (this.distinctValuesProvider) {
      // Fetched when the user first opens this filter, so loading a grid does not fire one request per column.
      this.useDerivedOptions = false;
    } else {
      this.useDerivedOptions = true;
      this.refreshDerivedOptions();
    }
    const headerName = params.column.getColDef().headerName;
    this.filterPlaceholder = 'Select ';
    if (headerName.endsWith('y')) {
      this.filterPlaceholder += headerName.slice(0, -1) + 'ies';
    } else if (headerName.endsWith('s')) {
      this.filterPlaceholder += headerName;
    } else {
      this.filterPlaceholder += headerName + 's';
    }
  }

  /**
   * Called by AG Grid every time this filter's popup is (re)shown. Grid-derived option lists are
   * recomputed here so a value newly entered into a cell (which isn't yet in the filterable set)
   * shows up the next time the user opens the dropdown, without requiring a full page reload.
   * Fixed/enum-style option lists (cellEditorParams.availableOptions) are intentionally left alone.
   */
  afterGuiAttached(): void {
    if (!this.guiAttached()) {
      Promise.resolve().then(() => this.guiAttached.set(true));
    }
    if (this.useDerivedOptions) {
      this.refreshDerivedOptions();
    } else if (this.distinctValuesProvider && !this.serverOptionsLoaded) {
      this.loadServerOptions();
    }
  }

  /** Asks the server for every distinct value in this column, so the dropdown offers values that are not
   * on the loaded page. Kept for the life of the filter: the set only changes when the data does. */
  private loadServerOptions(): void {
    this.serverOptionsLoaded = true;
    this.distinctValuesProvider().subscribe({
      next: (values) => {
        this.options = CustomMultiSelectFilterComponent.sortOptions((values ?? [])
            .filter(value => value != null && String(value).length > 0)
            .map(value => ({label: String(value), value: String(value)})));
      },
      error: () => {
        // Let the user retry by reopening the filter rather than leaving it permanently empty.
        this.serverOptionsLoaded = false;
      }
    });
  }

  private refreshDerivedOptions(): void {
    this.options = CustomMultiSelectFilterComponent.sortOptions(this.getUniqueValues()
        .filter(value => value != null && typeof value !== 'object') // Filter out null, undefined, and objects
        .map(value => ({label: String(value), value: String(value)})));
  }

  /** Sorts filter dropdown options by label so values are always presented in a stable, predictable
   * order regardless of the order they were discovered in (grid row order, list order, etc.). Uses a
   * locale-aware numeric comparison so alphanumeric values (e.g. wire sizes) sort naturally. */
  private static sortOptions(options: { label: string, value: string }[]): { label: string, value: string }[] {
    return [...options].sort((a, b) => a.label.localeCompare(b.label, undefined, {numeric: true, sensitivity: 'base'}));
  }

  onSelectionChange(): void {
    // call the grid api filter change method to refresh the grid
    this.params.api.onFilterChanged();
  }

  onSelectedValuesChanged(newValues: string[] | null): void {
    if (this.disabled()) {
      return;
    }
    this.selectedValues = this.normalizeSelectedPrimitives(newValues);
    this.params.api.onFilterChanged();
  }

  disabled(): boolean {
    return this.isLocked();
  }

  showClear(): boolean {
    return !this.disabled();
  }

  /** PrimeNG may emit objects for legacy state; keep filter model as primitive strings. */
  private normalizeSelectedPrimitives(values: any[] | null | undefined): string[] {
    if (!values?.length) {
      return [];
    }
    return values.map((v) => {
      if (v != null && typeof v === 'object' && 'value' in v) {
        return String((v as { value: unknown }).value);
      }
      return String(v);
    });
  }

  isFilterActive(): boolean {
    return this.selectedValues?.length > 0;
  }

  getModel(): any {
    return this.selectedValues && this.selectedValues.length > 0
            ? { values: this.selectedValues } : null;
  }

  setModel(model: any): void {
    this.selectedValues = this.normalizeSelectedPrimitives(model?.values);
  }

  doesFilterPass(params: IDoesFilterPassParams): boolean {
    if (this.serverSideFiltering) {
      // The rows in memory came back from a query that already applied this filter.
      return true;
    }
    if (!this.selectedValues || this.selectedValues.length === 0) {
      return true;
    }
    const cellVals = this.cellValuesOf(this.params.getValue(params.node));
    if (cellVals.length === 0) {
      // Nothing to compare, which no chosen value can be found in.
      return false;
    }
    // A column that holds several values at once is asked for rows holding all of the chosen ones, not any.
    if (this.multiSelectField) {
      return this.selectedValues.every((selectedValue) => cellVals.includes(selectedValue));
    }
    return this.selectedValues.includes(cellVals[0]);
  }

  /**
   * The values one cell holds, as this filter compares them. A cell can carry plain text, a reference object, or a
   * list of either, and a column holding several values at once writes them into a single delimited string - so what
   * the filter compares against is always a list, of one value where the column holds one.
   */
  private cellValuesOf(cellValue: any): string[] {
    if (cellValue == null) {
      return [];
    }
    const allowedValueKey = this.params.column.getColDef().cellEditorParams?.allowedValueKey as string | undefined;
    if (Array.isArray(cellValue)) {
      return CustomMultiSelectFilterComponent.valuesOfList(cellValue, allowedValueKey);
    }
    if (typeof cellValue === 'object') {
      // Without a key naming the field to read, an object has no value this filter could compare.
      return allowedValueKey ? CustomMultiSelectFilterComponent.valueOfReference(cellValue, allowedValueKey) : [];
    }
    return this.multiSelectField
           ? DropdownHelperService.splitMultiSelectValue(String(cellValue))
           : [String(cellValue).trim()];
  }

  /** A cell holding a list: of reference objects when the column names the field to read, of plain values otherwise. */
  private static valuesOfList(cellValue: any[], allowedValueKey?: string): string[] {
    const first = cellValue[0];
    if (first != null && typeof first === 'object' && allowedValueKey) {
      return cellValue.map((o) => String(DropdownHelperService.extractValueByKey(o, allowedValueKey)).trim())
                      .filter((v) => v.length > 0);
    }
    return DropdownHelperService.splitMultiSelectValue(cellValue);
  }

  /** A cell holding one reference object, read through the field the column names as its value. */
  private static valueOfReference(cellValue: any, allowedValueKey: string): string[] {
    const extracted = DropdownHelperService.extractValueByKey(cellValue, allowedValueKey);
    return (extracted == null || extracted === '') ? [] : [String(extracted).trim()];
  }

  getUniqueValues(): string[] {
    const uniqueValues = new Set<string>();
    this.params.api.forEachNode(node => {
      const value = this.params.getValue(node);
      // Only add valid values (not null, undefined, or objects)
      if (value != null && typeof value !== 'object') {
        uniqueValues.add(String(value));
      }
    });
    return Array.from(uniqueValues);
  }

}
